import React from "react";
import { useQuery } from "react-query";
import { dashboardService } from "@/services/dashboardService";
import { useAuth } from "@/contexts/AuthContext";
import { selectApiData } from "@/services/api";
import StatsCard from "./components/StatsCard";
import RecentActivities from "./components/RecentActivities";
import ProjectsOverview from "./components/ProjectsOverview";
import TasksOverview from "./components/TasksOverview";
import QuickActions from "./components/QuickActions";
import FinancialOverview from "./components/FinancialOverview";
import HROverview from "./components/HROverview";
import EmployeesOnLeave from "./components/EmployeesOnLeave";
import InventoryOverview from "./components/InventoryOverview";
import NotificationPanel from "./components/NotificationPanel";
import AlertsPanel from "./components/AlertsPanel";
import DebugInfo from "@/components/DebugInfo";
import APITest from "@/components/APITest";
import DashboardTest from "@/components/DashboardTest";
import TokenTest from "@/components/TokenTest";
import { AllAnalyticsCharts } from "./components/AnalyticsCharts";

const Dashboard = () => {
  const { user } = useAuth();

  // console.log('Test Query State:', { testQuery, testLoading, testError });

  // Fetch dashboard data
  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
    error: dashboardError,
    refetch: refetchDashboard,
  } = useQuery(
    "dashboard-data",
    () => {
      return dashboardService.getProjectsOverview();
    },
    {
      enabled: !!user,
      select: selectApiData,
      refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
      cacheTime: 0, // No caching
      staleTime: 0, // Always fetch fresh
      onError: (error) => {
        console.error("Dashboard data fetch error:", error);
      },
    },
  );

  const {
    data: statsData,
    isLoading: isStatsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useQuery(
    "dashboard-stats",
    () => {
      return dashboardService.getStats();
    },
    {
      enabled: !!user,
      select: selectApiData,
      refetchInterval: 2 * 60 * 1000, // Refetch every 2 minutes
      cacheTime: 0, // No caching
      staleTime: 0, // Always fetch fresh
      onError: (error) => {
        console.error("Stats data fetch error:", error);
      },
    },
  );

  const { data: notificationsData, error: notificationsError } = useQuery(
    "dashboard-notifications",
    () => {
      return dashboardService.getNotifications();
    },
    {
      enabled: !!user,
      select: selectApiData,
      cacheTime: 0, // No caching
      staleTime: 0, // Always fetch fresh
      refetchInterval: 30 * 1000, // Refetch every 30 seconds
      onError: (error) => {
        console.error("Notifications fetch error:", error);
      },
    },
  );

  const { data: alertsData, error: alertsError } = useQuery(
    "dashboard-alerts",
    () => {
      console.log("Alerts query function called");
      return dashboardService.getAlerts();
    },
    {
      enabled: !!user,
      select: selectApiData,
      cacheTime: 0, // No caching
      staleTime: 0, // Always fetch fresh
      refetchInterval: 60 * 1000, // Refetch every minute
      onError: (error) => {
        console.error("Alerts fetch error:", error);
      },
    },
  );

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  // Show errors if they exist
  if (dashboardError) {
    console.error("Dashboard data error:", dashboardError);
  }

  if (statsError) {
    console.error("Stats data error:", statsError);
  }

  // Force show content for debugging
  const forceShowContent = true;

  if ((isDashboardLoading || isStatsLoading) && !forceShowContent) {
    return (
      <div className="space-y-6">
        {/* Loading skeleton */}
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded w-1/4 mb-2"></div>
          <div className="h-4 bg-gray-300 rounded w-1/2"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="bg-gray-300 h-32 rounded-lg"></div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="animate-pulse bg-gray-300 h-64 rounded-lg"></div>
            <div className="animate-pulse bg-gray-300 h-64 rounded-lg"></div>
          </div>
          <div className="space-y-6">
            <div className="animate-pulse bg-gray-300 h-32 rounded-lg"></div>
            <div className="animate-pulse bg-gray-300 h-64 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Greeting Banner */}
      <div
        className="bg-white rounded-2xl p-6 mb-6"
        style={{ border: '0.5px solid #e5e7eb' }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-medium text-gray-900 mb-1">
              {getGreeting()}, {user?.first_name || "User"}! 👋
            </h1>
            <p className="text-gray-500 flex items-center gap-2 text-sm">
              <span>📅</span>
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>
          {user?.UserType?.display_name && (
            <div className="px-4 py-2 bg-orange-50 text-[#DF6B38] rounded-lg text-sm font-medium">
              {user.UserType.display_name}
            </div>
          )}
        </div>
      </div>

      {/* Employees on Leave - shown at the top so it's immediately visible */}
      <div className="mb-6">
        <EmployeesOnLeave limit={8} />
      </div>

      {/* Alerts Panel
      {alertsData?.alerts?.length > 0 && (
        <AlertsPanel alerts={alertsData.alerts} />
      )} */}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {(() => {
          return null;
        })()}
        <StatsCard
          title="Total Projects"
          value={
            statsData?.projects?.total !== undefined
              ? statsData?.projects?.total.toString()
              : "0"
          }
          change={statsData?.projects?.change || "0%"}
          changeType={statsData?.projects?.changeType || "neutral"}
          icon="projects"
          loading={isStatsLoading}
        />
        <StatsCard
          title="Active Tasks"
          value={
            statsData?.tasks?.active !== undefined
              ? statsData?.tasks?.active.toString()
              : "0"
          }
          change={statsData?.tasks?.change || "0%"}
          changeType={statsData?.tasks?.changeType || "neutral"}
          icon="tasks"
          loading={isStatsLoading}
        />
        <StatsCard
          title="Team Members"
          value={
            statsData?.employees?.total !== undefined
              ? statsData?.employees?.total.toString()
              : "0"
          }
          change={statsData?.employees?.change || "0%"}
          changeType={statsData?.employees?.changeType || "neutral"}
          icon="users"
          loading={isStatsLoading}
        />
        <StatsCard
          title="Revenue"
          value={statsData?.revenue?.total || "₦0"}
          change={statsData?.revenue?.change || "0%"}
          changeType={statsData?.revenue?.changeType || "neutral"}
          icon="revenue"
          loading={isStatsLoading}
        />
      </div>

      {/* Analytics Charts Section */}
      <div className="bg-white rounded-2xl p-6 border mb-6" style={{ borderColor: '#e5e7eb' }}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Analytics Overview
            </h2>
            <p className="text-gray-500 mt-1 text-sm">
              Visual insights into your business performance
            </p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-orange-50 text-[#DF6B38] text-xs font-medium">
              📊 Live Data
            </span>
          </div>
        </div>
        <AllAnalyticsCharts />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          <ProjectsOverview />
          <TasksOverview />
          <FinancialOverview />
          <InventoryOverview />
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          <QuickActions />
          {notificationsData?.notifications && (
            <NotificationPanel
              notifications={notificationsData.notifications}
            />
          )}
          <HROverview />
          <RecentActivities />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
